import React, { useCallback, useRef, useState } from "react";
import {
  Button,
  CopyButton,
  EmbedIcon,
  InfoTooltip,
  Logo,
  ReactIcon,
  SimpleTooltipContent,
} from "@panda0/panda0-ui";
import { AnimatePresence, motion } from "framer-motion";

// Icons for the embed options
const InlineIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <rect
      x="6"
      y="8"
      width="12"
      height="8"
      rx="1"
      fill="currentColor"
      opacity="0.4"
    />
  </svg>
);

const ButtonIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="8"
      width="16"
      height="8"
      rx="4"
      fill="currentColor"
      opacity="0.4"
    />
    <path
      d="M4 12C4 9.79086 5.79086 8 8 8H16C18.2091 8 20 9.79086 20 12C20 14.2091 18.2091 16 16 16H8C5.79086 16 4 14.2091 4 12Z"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const LinkIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 13.5C10.8284 14.3284 12.1716 14.3284 13 13.5L17.5 9C18.3284 8.17157 18.3284 6.82843 17.5 6C16.6716 5.17157 15.3284 5.17157 14.5 6L13 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M14 10.5C13.1716 9.67157 11.8284 9.67157 11 10.5L6.5 15C5.67157 15.8284 5.67157 17.1716 6.5 18C7.32843 18.8284 8.67157 18.8284 9.5 18L11 16.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const PopupIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="6"
      width="16"
      height="12"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M8 9H16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8 12H14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8 15H12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// Embed code templates
function getInlineEmbedCode(token: string) {
  return `<!-- HirePanda Inline Quiz Embed -->
<div id="panda-quiz"></div>
<script src="https://hirepandacdn.com/embed/script.js"></script>
<script>
  Pqz.init({
    token: "${token}",
    data: "quiz",
    displayMode: "inline",
    root: document.getElementById("panda-quiz"),
    size: { height: "600px" }
  });
</script>`;
}

function getButtonEmbedCode(token: string) {
  return `<!-- HirePanda Button Quiz Embed -->
<div id="panda-quiz"></div>
<script src="https://hirepandacdn.com/embed/script.js"></script>
<script>
  Pqz.init({
    token: "${token}",
    data: "quiz",
    displayMode: "button",
    buttonText: "Take PandaQuiz!",
    root: document.getElementById("panda-quiz")
  });
</script>`;
}

function getLinkEmbedCode(token: string) {
  return `<!-- HirePanda Link Quiz Embed -->
<div id="panda-quiz"></div>
<script src="https://hirepandacdn.com/embed/script.js"></script>
<script>
  Pqz.init({
    token: "${token}",
    data: "quiz",
    displayMode: "link",
    buttonText: "Take PandaQuiz!",
    root: document.getElementById("panda-quiz")
  });
</script>`;
}

function getPopupEmbedCode(token: string) {
  return `<!-- HirePanda Popup Quiz Embed -->
<div id="panda-quiz"></div>
<script src="https://hirepandacdn.com/embed/script.js"></script>
<script>
  Pqz.init({
    token: "${token}",
    data: "quiz",
    displayMode: "popup",
    buttonText: "Open PandaQuiz!",
    modalOverlay: true,
    root: document.getElementById("panda-quiz")
  });
</script>`;
}

// React component embed code templates
function getReactInlineEmbedCode(token: string) {
  return `// HirePanda Inline Quiz Embed
import { InlineEmbed } from "@hirepanda/quiz-embed";

export default function QuizComponent() {
  return (
    <InlineEmbed
      token="${token}"
      data="quiz"
      style={{ height: "600px" }}
    />
  );
}`;
}

function getReactButtonEmbedCode(token: string) {
  return `// HirePanda Button Quiz Embed
import { ButtonEmbed } from "@hirepanda/quiz-embed";

export default function QuizButton() {
  return (
    <ButtonEmbed
      token="${token}"
      data="quiz"
      buttonText="Take PandaQuiz!"
    />
  );
}`;
}

function getReactLinkEmbedCode(token: string) {
  return `// HirePanda Link Quiz Embed
import { LinkEmbed } from "@hirepanda/quiz-embed";

export default function QuizLink() {
  return (
    <LinkEmbed
      token="${token}"
      data="quiz"
      buttonText="Take PandaQuiz!"
    />
  );
}`;
}

function getReactPopupEmbedCode(token: string) {
  return `// HirePanda Popup Quiz Embed
import { PopupEmbed } from "@hirepanda/quiz-embed";

export default function QuizPopup() {
  const handleQuizComplete = (results) => {
    console.log("Quiz completed:", results);
  };

  return (
    <PopupEmbed
      token="${token}"
      data="quiz"
      buttonText="Open PandaQuiz!"
      modalOverlay={true}
      onQuizComplete={handleQuizComplete}
    />
  );
}`;
}

// Preview component for each embed type
const EmbedPreview = ({ type }: { type: string }) => {
  let content;

  switch (type) {
    case "inline":
      content = (
        <div className="rounded-lg border border-neutral-200 bg-white p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="p-4 bg-neutral-50 rounded-lg mb-2">
              <div className="flex justify-center">
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={17}
                  height={17}
                  fill="none"
                  className="h-10 w-10 text-black"
                >
                  <rect
                    width={16}
                    height={16}
                    x={0.898}
                    y={0.767}
                    fill="#fff"
                    rx={2.5}
                  />
                  <path
                    fill="#010101"
                    d="M8.68 10.8H7.663c-.51 0-.769.577-.41.92l1.018.977a.601.601 0 0 0 .822 0l1.019-.977c.357-.343.1-.92-.41-.92H8.68Z"
                  />
                  <path
                    fill="#000"
                    stroke="#010101"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={0.25}
                    d="M10.918 12.938c-2.658 2.613-4.62 0-4.62 0s2.57 1.407 2.328-1.063c0 0-.26 2.569 2.292 1.063Z"
                  />
                  <path
                    fill="#010101"
                    stroke="#000"
                    d="M2.108 9.805v2.713h1.558s.108-1.802.108-2.237c0-.436 2.67-1.782 2.67-1.782.602-.327.843-.653 1.204-1.307.362-.653.489-1.453.241-1.96-.485-.992-1.686-1.415-2.77-.762S2.11 6.865 2.11 7.736v2.07ZM15.687 9.805v.745h-1.665v-.269c0-.436-2.67-1.782-2.67-1.782-.603-.327-.844-.653-1.205-1.307-.361-.653-.489-1.453-.24-1.96.484-.992 1.685-1.415 2.77-.762 1.083.653 3.01 2.395 3.01 3.266v2.07Z"
                  />
                  <rect
                    width={0.965}
                    height={1.042}
                    x={7.226}
                    y={6.231}
                    fill="#fff"
                    rx={0.45}
                    transform="rotate(38.562 7.226 6.231)"
                  />
                  <rect
                    width={0.965}
                    height={1.042}
                    x={9.809}
                    y={6.833}
                    fill="#fff"
                    rx={0.45}
                    transform="rotate(-38.56 9.809 6.833)"
                  />
                </svg>
              </div>
            </div>
            <p className="text-sm text-neutral-500">
              Full quiz embedded directly in your page
            </p>
          </div>
        </div>
      );
      break;
    case "button":
      content = (
        <div className="rounded-lg border border-neutral-200 bg-white p-4 flex flex-col items-center justify-center">
          <button className="px-5 py-3 bg-[#4CB932] text-white rounded-lg shadow flex items-center gap-2 hover:bg-[#5dd840] transition-colors">
            <Logo className="h-6 w-6 text-white" />
            <span className="font-medium">Take PandaQuiz!</span>
          </button>
          <p className="text-sm text-neutral-500 mt-4">
            Button that opens in a new tab
          </p>
        </div>
      );
      break;
    case "link":
      content = (
        <div className="rounded-lg border border-neutral-200 bg-white p-4 flex flex-col items-center justify-center">
          <a
            href="#"
            className="text-[#4CB932] font-medium flex items-center gap-1 hover:underline"
          >
            <Logo className="h-4 w-4 text-[#4CB932]" />
            Take PandaQuiz!
          </a>
          <p className="text-sm text-neutral-500 mt-4">
            Simple link to your quiz
          </p>
        </div>
      );
      break;
    case "popup":
      content = (
        <div className="rounded-lg border border-neutral-200 bg-white p-4 flex flex-col items-center justify-center">
          <button className="px-5 py-3 bg-[#4CB932] text-white rounded-lg shadow flex items-center gap-2 hover:bg-[#5dd840] transition-colors">
            <Logo className="h-6 w-6 text-white" />
            <span className="font-medium">Open PandaQuiz!</span>
          </button>
          <div className="flex items-center gap-2 mt-4">
            <div className="w-3 h-3 bg-neutral-200 rounded-full"></div>
            <p className="text-sm text-neutral-500">
              Opens in a popup on the same page
            </p>
          </div>
        </div>
      );
      break;
    default:
      content = null;
  }

  return content;
};

// Title and description for each embed type
const embedTypes = {
  inline: {
    title: "Inline Embed",
    description:
      "Embed the complete quiz directly within your page content. Best for dedicated assessment pages.",
    icon: <InlineIcon />,
  },
  button: {
    title: "Button Embed",
    description:
      "Add a styled button that opens the quiz in a new tab. Good for CTAs in existing page flows.",
    icon: <ButtonIcon />,
  },
  link: {
    title: "Link Embed",
    description:
      "Simple text link that opens the quiz in a new tab. Minimal and unobtrusive.",
    icon: <LinkIcon />,
  },
  popup: {
    title: "Popup Embed",
    description:
      "Button that opens the quiz in a modal popup on the same page. Keeps users on your site.",
    icon: <PopupIcon />,
  },
};

// Main component
function EmbedSharingTab({ key }) {
  const [selectedEmbed, setSelectedEmbed] = useState<string>("inline");
  const [codeType, setCodeType] = useState<"html" | "react">("html");

  const getCode = useCallback(
    (type: string, codeType: "html" | "react", token: string) => {
      if (codeType === "html") {
        switch (type) {
          case "inline":
            return getInlineEmbedCode(token);
          case "button":
            return getButtonEmbedCode(token);
          case "link":
            return getLinkEmbedCode(token);
          case "popup":
            return getPopupEmbedCode(token);
          default:
            return "";
        }
      } else {
        switch (type) {
          case "inline":
            return getReactInlineEmbedCode(token);
          case "button":
            return getReactButtonEmbedCode(token);
          case "link":
            return getReactLinkEmbedCode(token);
          case "popup":
            return getReactPopupEmbedCode(token);
          default:
            return "";
        }
      }
    },
    []
  );

  const code = getCode(selectedEmbed, codeType, `pqz_embed_${key}`);

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-xl bg-[#F8FFF5] border border-[#E0F5D7]">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#4CB932] rounded-lg">
            <Logo className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-medium text-[#2A7016]">
              Enhance your candidate experience
            </h3>
            <p className="text-sm text-[#4D7043] mt-1">
              Choose how to embed the Panda Quiz in your website. Copy the code
              and paste it into your site.
            </p>
          </div>
        </div>
      </div>

      {/* Embed Type Selection Buttons */}
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(embedTypes).map(([type, { title, icon }]) => (
          <button
            key={type}
            onClick={() => setSelectedEmbed(type)}
            className={`flex flex-col items-center justify-center p-4 rounded-xl border ${
              selectedEmbed === type
                ? "border-[#4CB932] bg-[#F8FFF5] text-[#2A7016]"
                : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
            } transition-colors`}
          >
            <div
              className={`p-2 rounded-full ${
                selectedEmbed === type
                  ? "bg-[#4CB932] text-white"
                  : "bg-neutral-100 text-neutral-600"
              }`}
            >
              {icon}
            </div>
            <span className="mt-2 font-medium text-sm">{title}</span>
          </button>
        ))}
      </div>

      {/* Selected Embed Details */}
      <div className="flex flex-col">
        <motion.div
          key={selectedEmbed}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-row w-full justify-between"
        >
          <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 w-[40%]">
            <div className="space-y-5">
              <div className="border-b border-neutral-100 pb-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  {embedTypes[selectedEmbed].title}
                  <InfoTooltip
                    content={
                      <SimpleTooltipContent
                        title={embedTypes[selectedEmbed].description}
                        cta="Learn more about embed options"
                        href="#"
                      />
                    }
                  />
                </h3>
                <p className="text-sm text-neutral-500 mt-2">
                  {embedTypes[selectedEmbed].description}
                </p>
              </div>
              <div className="pt-2">
                <EmbedPreview type={selectedEmbed} />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white w-[58%]">
            <div className="border-b border-neutral-100 px-4 py-3 flex justify-between items-center">
              <div className="flex w-full justify-between items-center">
                <div className="flex items-center gap-2">
                  <Button
                    variant={codeType === "html" ? "primary" : "secondary"}
                    icon={<EmbedIcon className="h-5 w-5" />}
                    text="HTML"
                    className="h-8 gap-1 px-2.5"
                    onClick={() => setCodeType("html")}
                  />
                  <Button
                    variant={codeType === "react" ? "primary" : "secondary"}
                    icon={<ReactIcon className="h-5 w-5" />}
                    text="React"
                    className="h-8 gap-1 px-2.5"
                    onClick={() => setCodeType("react")}
                  />
                </div>
                <CopyButton value={code} />
              </div>
            </div>
            <div className="p-4 bg-neutral-50 rounded-b-xl">
              <pre className="text-xs overflow-x-auto p-3 bg-neutral-100 rounded-lg max-h-[240px]">
                <code>{code}</code>
              </pre>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Help section */}
      <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200 mt-6">
        <h3 className="font-medium">Need help with integration?</h3>
        <p className="text-sm text-neutral-600 mt-1">
          Our team can help you customize and integrate the quiz into your
          website.
          <a href="#" className="text-[#4CB932] ml-1 hover:underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}

export default React.memo(EmbedSharingTab);
